import { useCallback, useEffect, useState } from 'react';
import { Lock, Plus, ShieldCheck, Unlock, CheckCircle2 } from 'lucide-react';
import { usePlant } from '../../state/PlantContext';
import { useAuth } from '../../state/AuthContext';
import { isolationApi, permitApi, plantApi, ApiError } from '../../lib/api';
import type { IsolationCertificateSummary, IsolationPoint, Lockbox, PermitListItem, PlantNode } from '../../lib/types';
import { Spinner, EmptyState, ErrorBanner, DisclosureBanner } from '../../components/Misc';
import { Modal } from '../../components/Modal';

const CERT_ROLES = ['safety_officer', 'area_authority', 'org_admin', 'platform_admin'];
const VERIFY_ROLES = ['safety_officer', 'area_authority', 'platform_admin'];
const LOCK_ROLES = ['performing_authority', 'area_authority', 'safety_officer', 'org_admin', 'platform_admin'];

export default function IsolationPage() {
  const { currentPlant } = usePlant();
  const { user } = useAuth();
  const [certs, setCerts] = useState<IsolationCertificateSummary[]>([]);
  const [nodes, setNodes] = useState<PlantNode[]>([]);
  const [permits, setPermits] = useState<PermitListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [newCertOpen, setNewCertOpen] = useState(false);

  const load = useCallback(async () => {
    if (!currentPlant) return;
    setError(null);
    try {
      const [certRes, permitRes, topo] = await Promise.all([
        isolationApi.list(currentPlant.id),
        permitApi.list(currentPlant.id),
        plantApi.topology(currentPlant.id),
      ]);
      setCerts(certRes.certificates);
      setPermits(permitRes.permits);
      setNodes(topo.nodes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load isolation certificates.');
    } finally {
      setLoading(false);
    }
  }, [currentPlant]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  if (!currentPlant) return <Spinner label="Loading plant…" />;
  if (loading) return <Spinner label="Loading isolation certificates…" />;

  const canCreateCert = user && CERT_ROLES.includes(user.role);

  return (
    <div className="stack gap-5">
      <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="h2-section">Isolation &amp; LOTO</h1>
          <p className="body-text text-secondary">Lockout/tagout certificates, isolation points, and group lockboxes.</p>
        </div>
        <div className="spacer" />
        {canCreateCert && (
          <button className="btn btn-primary" onClick={() => setNewCertOpen(true)}>
            <Plus size={16} /> New certificate
          </button>
        )}
      </div>

      {error && <ErrorBanner message={error} />}

      {certs.length === 0 ? (
        <EmptyState icon={<Lock size={32} />} title="No isolation certificates yet" description="Create one to register isolation points and lockboxes for LOTO-controlled work." />
      ) : (
        <div className="stack gap-3">
          {certs.map((c) => (
            <div key={c.id} className="card">
              <div className="row" style={{ cursor: 'pointer' }} onClick={() => setSelected(selected === c.id ? null : c.id)}>
                <div className="stack gap-1">
                  <span className="mono" style={{ fontWeight: 700 }}>{c.certificateNumber}</span>
                  {c.description && <span className="body-small text-tertiary">{c.description}</span>}
                </div>
                <div className="spacer" />
                <span className={`status-badge ${c.status === 'RELEASED' ? 'neutral' : c.status === 'VERIFIED' ? 'clear' : c.status === 'APPLIED' ? 'advisory' : 'neutral'}`}>{c.status}</span>
              </div>
              {selected === c.id && (
                <CertificateDetail
                  cert={c}
                  plantId={currentPlant.id}
                  nodes={nodes}
                  permits={permits}
                  userRole={user?.role}
                  onChanged={load}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {newCertOpen && (
        <NewCertModal
          onClose={() => setNewCertOpen(false)}
          onSubmit={async (input) => {
            if (!currentPlant) return;
            await isolationApi.create(currentPlant.id, input);
            setNewCertOpen(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function CertificateDetail({ cert, plantId, nodes, permits, userRole, onChanged }: { cert: IsolationCertificateSummary; plantId: string; nodes: PlantNode[]; permits: PermitListItem[]; userRole?: string; onChanged: () => Promise<void> }) {
  const [points, setPoints] = useState<IsolationPoint[] | null>(null);
  const [lockboxes, setLockboxes] = useState<Lockbox[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [addPointOpen, setAddPointOpen] = useState(false);
  const [addLockboxOpen, setAddLockboxOpen] = useState(false);
  const [lockingBox, setLockingBox] = useState<Lockbox | null>(null);

  const canVerify = userRole && VERIFY_ROLES.includes(userRole);
  const canLock = userRole && LOCK_ROLES.includes(userRole);
  const canAddPoint = userRole && CERT_ROLES.includes(userRole);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [p, l] = await Promise.all([isolationApi.points(plantId, cert.id), isolationApi.lockboxes(plantId, cert.id)]);
      setPoints(p.points);
      setLockboxes(l.lockboxes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load certificate detail.');
    }
  }, [plantId, cert.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  }

  if (points === null || lockboxes === null) return <div style={{ padding: 16 }}><Spinner label="Loading…" /></div>;

  return (
    <div className="stack gap-4" style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
      {error && <ErrorBanner message={error} />}

      <div className="stack gap-2">
        <div className="row">
          <span className="body-small" style={{ fontWeight: 700 }}>Isolation points</span>
          <div className="spacer" />
          {canAddPoint && <button className="btn btn-ghost btn-sm" onClick={() => setAddPointOpen(true)}><Plus size={14} /> Add point</button>}
        </div>
        {points.length === 0 ? (
          <p className="body-small text-tertiary">No isolation points registered on this certificate yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Tag</th><th>Node</th><th>Type</th><th>State</th><th>Try-out</th><th></th></tr></thead>
              <tbody>
                {points.map((pt) => (
                  <tr key={pt.id}>
                    <td data-label="Tag"><span className="mono">{pt.nodeTagNumber}</span></td>
                    <td data-label="Node">{pt.nodeName}</td>
                    <td data-label="Type">{pt.isolationType.replace(/_/g, ' ')}</td>
                    <td data-label="State"><span className={`pill`}>{pt.currentState}</span></td>
                    <td data-label="Try-out">{pt.tryoutConfirmed ? <CheckCircle2 size={16} color="var(--status-clear)" /> : '—'}</td>
                    <td data-label="">
                      {canVerify && pt.currentState !== 'VERIFIED' && (
                        <button className="btn btn-secondary btn-sm" disabled={busy === `verify-${pt.id}`} onClick={() => run(`verify-${pt.id}`, () => isolationApi.verifyPoint(plantId, cert.id, pt.id))}>
                          <ShieldCheck size={14} /> Verify
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="stack gap-2">
        <div className="row">
          <span className="body-small" style={{ fontWeight: 700 }}>Group lockboxes</span>
          <div className="spacer" />
          {canAddPoint && <button className="btn btn-ghost btn-sm" onClick={() => setAddLockboxOpen(true)}><Plus size={14} /> Add lockbox</button>}
        </div>
        {lockboxes.length === 0 ? (
          <p className="body-small text-tertiary">No lockboxes on this certificate.</p>
        ) : (
          <div className="stack gap-2">
            {lockboxes.map((lb) => (
              <div key={lb.id} className="row gap-3" style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: '10px 14px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>{lb.name}</span>
                <span className="pill">{lb.status}</span>
                <span className="body-small text-tertiary">{lb.activeLocks.length} active lock{lb.activeLocks.length === 1 ? '' : 's'}</span>
                <div className="spacer" />
                {canLock && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setLockingBox(lb)}>
                    <Lock size={14} /> Apply lock
                  </button>
                )}
                {canLock && lb.activeLocks.map((lock) => (
                  <button key={lock.id} className="btn btn-ghost btn-sm" disabled={busy === `release-${lock.id}`}
                    onClick={() => lock.permitId && run(`release-${lock.id}`, () => isolationApi.releaseLock(plantId, cert.id, lb.id, lock.id, lock.permitId!))}>
                    <Unlock size={14} /> Release
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {addPointOpen && (
        <AddPointModal
          nodes={nodes}
          onClose={() => setAddPointOpen(false)}
          onSubmit={async (input) => { await run('add-point', () => isolationApi.addPoint(plantId, cert.id, input)); setAddPointOpen(false); }}
        />
      )}
      {addLockboxOpen && (
        <AddLockboxModal
          onClose={() => setAddLockboxOpen(false)}
          onSubmit={async (name) => { await run('add-lockbox', () => isolationApi.createLockbox(plantId, cert.id, name)); setAddLockboxOpen(false); }}
        />
      )}
      {lockingBox && (
        <ApplyLockModal
          lockbox={lockingBox}
          permits={permits}
          onClose={() => setLockingBox(null)}
          onSubmit={async (permitId) => { await run('apply-lock', () => isolationApi.applyLock(plantId, cert.id, lockingBox.id, permitId)); setLockingBox(null); }}
        />
      )}
    </div>
  );
}

function NewCertModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (input: { certificateNumber: string; description?: string }) => Promise<void> }) {
  const [certificateNumber, setCertificateNumber] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal title="New isolation certificate" onClose={onClose}>
      <div className="stack gap-3">
        <div className="field"><label>Certificate number</label><input value={certificateNumber} onChange={(e) => setCertificateNumber(e.target.value)} placeholder="LOTO-2026-041" /></div>
        <div className="field"><label>Description (optional)</label><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        <div className="row gap-2" style={{ marginTop: 8 }}>
          <button className="btn btn-primary" disabled={submitting || !certificateNumber.trim()} onClick={async () => { setSubmitting(true); try { await onSubmit({ certificateNumber, description: description || undefined }); } finally { setSubmitting(false); } }}>
            {submitting ? 'Creating…' : 'Create certificate'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function AddPointModal({ nodes, onClose, onSubmit }: { nodes: PlantNode[]; onClose: () => void; onSubmit: (input: { nodeId: string; isolationType: string; requiredState: string }) => Promise<void> }) {
  const [nodeId, setNodeId] = useState('');
  const [isolationType, setIsolationType] = useState('VALVE_CLOSED');
  const [requiredState, setRequiredState] = useState('CLOSED');
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal title="Add isolation point" onClose={onClose}>
      <div className="stack gap-3">
        <DisclosureBanner>Only nodes flagged as energy sources or positive-isolation points should be registered here — this list is not filtered.</DisclosureBanner>
        <div className="field">
          <label>Equipment node</label>
          <select value={nodeId} onChange={(e) => setNodeId(e.target.value)}>
            <option value="">Select a node…</option>
            {nodes.map((n) => <option key={n.id} value={n.id}>{n.tagNumber} — {n.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Isolation type</label>
          <select value={isolationType} onChange={(e) => setIsolationType(e.target.value)}>
            <option value="VALVE_CLOSED">Valve closed</option>
            <option value="SPECTACLE_BLIND">Spectacle blind</option>
            <option value="BREAKER_RACKED_OUT">Breaker racked out</option>
            <option value="BLEED_OPEN">Bleed open</option>
          </select>
        </div>
        <div className="field"><label>Required state</label><input value={requiredState} onChange={(e) => setRequiredState(e.target.value)} /></div>
        <div className="row gap-2" style={{ marginTop: 8 }}>
          <button className="btn btn-primary" disabled={submitting || !nodeId} onClick={async () => { setSubmitting(true); try { await onSubmit({ nodeId, isolationType, requiredState }); } finally { setSubmitting(false); } }}>
            {submitting ? 'Adding…' : 'Add point'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function AddLockboxModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (name: string) => Promise<void> }) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal title="Add lockbox" onClose={onClose}>
      <div className="stack gap-3">
        <div className="field"><label>Lockbox name</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group lockbox A" /></div>
        <div className="row gap-2" style={{ marginTop: 8 }}>
          <button className="btn btn-primary" disabled={submitting || !name.trim()} onClick={async () => { setSubmitting(true); try { await onSubmit(name); } finally { setSubmitting(false); } }}>
            {submitting ? 'Adding…' : 'Add lockbox'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function ApplyLockModal({ lockbox, permits, onClose, onSubmit }: { lockbox: Lockbox; permits: PermitListItem[]; onClose: () => void; onSubmit: (permitId: string) => Promise<void> }) {
  const [permitId, setPermitId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal title={`Apply lock — ${lockbox.name}`} onClose={onClose}>
      <div className="stack gap-3">
        <div className="field">
          <label>Permit</label>
          <select value={permitId} onChange={(e) => setPermitId(e.target.value)}>
            <option value="">Select a permit…</option>
            {permits.map((p) => <option key={p.id} value={p.id}>{p.permitNumber} — {p.scopeText.slice(0, 40)}</option>)}
          </select>
        </div>
        <div className="row gap-2" style={{ marginTop: 8 }}>
          <button className="btn btn-primary" disabled={submitting || !permitId} onClick={async () => { setSubmitting(true); try { await onSubmit(permitId); } finally { setSubmitting(false); } }}>
            {submitting ? 'Applying…' : 'Apply lock'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}
